import { bookings } from '@repletoryreach/sdk-api';
import { Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Switch, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../i18n/I18nProvider';
import { WEEKDAY_CODES } from '../lib/data';

const MUTED = 'var(--rr-color-text-muted)';
const MAX_SLOTS = 6;

/** The first problem with one day's slots, as a catalogue key, or null. Same rules as the business hours. */
export function hoursProblem(slots: readonly bookings.StaffHoursSlot[]): string | null {
  if (slots.some((s) => !/^\d{2}:\d{2}$/.test(s.opens) || !/^\d{2}:\d{2}$/.test(s.closes))) return 'settings.hours.error.time';
  if (slots.some((s) => s.opens >= s.closes)) return 'settings.hours.error.order';
  const sorted = [...slots].sort((a, b) => a.opens.localeCompare(b.opens));
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i]!.opens < sorted[i - 1]!.closes) return 'settings.hours.error.overlap';
  }
  return null;
}

/** A person's own weekly hours, laid out like the business hours card. */
export function WeekHoursEditor({ value, onChange, disabled }: {
  value: bookings.StaffHoursSlot[];
  onChange: (next: bookings.StaffHoursSlot[]) => void;
  disabled?: boolean;
}) {
  const t = useT();
  const daySlots = (d: bookings.Weekday) => value.filter((s) => s.day === d);
  const replaceDay = (d: bookings.Weekday, slots: { opens: string; closes: string }[]) =>
    onChange(WEEKDAY_CODES.flatMap((x) => (x === d ? slots.map((s) => ({ day: d, ...s })) : value.filter((s) => s.day === x))));

  return (
    <Stack gap="sm" data-testid="staff-hours">
      {WEEKDAY_CODES.map((d) => {
        const slots = daySlots(d);
        const on = slots.length > 0;
        const problem = on ? hoursProblem(slots) : null;
        const dayName = t(`settings.hours.day.${d}`);
        return (
          <Stack key={d} gap="xs" className="rr-bookings__row">
            <Group gap="sm" justify="space-between" wrap="wrap">
              <Switch
                label={dayName}
                checked={on}
                disabled={disabled}
                onChange={(next) => replaceDay(d, next ? [{ opens: '08:00', closes: '17:00' }] : [])}
              />
              {!on && <Text role="caption" c={MUTED}>{t('settings.hours.closed')}</Text>}
            </Group>
            {slots.map((s, i) => (
              <Group key={i} gap="xs" wrap="wrap" align="flex-end">
                <TextInput type="time" label={t('settings.hours.opens')} value={s.opens} disabled={disabled}
                  onChange={(e) => replaceDay(d, slots.map((x, j) => (j === i ? { opens: e.currentTarget.value, closes: x.closes } : { opens: x.opens, closes: x.closes })))} />
                <TextInput type="time" label={t('settings.hours.closes')} value={s.closes} disabled={disabled}
                  onChange={(e) => replaceDay(d, slots.map((x, j) => (j === i ? { opens: x.opens, closes: e.currentTarget.value } : { opens: x.opens, closes: x.closes })))} />
                {!disabled && slots.length > 1 && (
                  <Button size="xs" variant="subtle" color="red" aria-label={t('settings.hours.removeSlotFor', { day: dayName })}
                    onClick={() => replaceDay(d, slots.filter((_, j) => j !== i).map((x) => ({ opens: x.opens, closes: x.closes })))}>
                    {t('action.remove')}
                  </Button>
                )}
              </Group>
            ))}
            {problem && <Text role="caption" c="var(--rr-color-error)">{t(problem)}</Text>}
            {!disabled && on && slots.length < MAX_SLOTS && (
              <div>
                <Button size="xs" variant="subtle"
                  onClick={() => {
                    const last = slots[slots.length - 1]!;
                    const opens = last.closes < '22:00' ? last.closes : '13:00';
                    const h = Number(opens.slice(0, 2));
                    const closes = `${String(Math.min(h + 1, 23)).padStart(2, '0')}${opens.slice(2)}`;
                    replaceDay(d, [...slots.map((x) => ({ opens: x.opens, closes: x.closes })), { opens, closes }]);
                  }}>
                  {t('settings.hours.addSlot')}
                </Button>
              </div>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}
